<template lang="html">
  <div class="d-flex flex-column" style="gap: 8px">
    <div
      class="options d-flex"
      :class="{'justify-content': $vuetify.breakpoint.xsOnly ? 'start' : 'end'}"
      style="gap: 8px;"
    >
      <v-btn
        v-if="destroyedValues.length > 0"
        depressed
        font-weight="600"
        font-size="14px"
        color="bgGrayOutside"
        @click="restoreAllFields"
      >
        <v-icon left color="primary" :size="iconSize" class="mr-1">
          mdi-arrow-u-left-bottom
        </v-icon>
        <span>{{ $t('general.restore_all_lines') }}</span>
      </v-btn>
      <v-btn
        v-if="undestroyedValues.length > 0"
        depressed
        color="error"
        font-weight="600"
        font-size="14px"
        class="px-4"
        @click="removeAllFields"
      >
        <v-icon left color="white" :size="iconSize" class="mr-1">
          mdi-trash-can
        </v-icon>
        {{ $t('general.remove_all_lines') }}
      </v-btn>
    </div>
    <template v-if="show">
      <component
        :is="show"
        v-model="localValue"
        :custom-resource="resource"
        :showData="showData"
        :nested-form-dialog="showForm"
        :nested-form-previous-item-index="previousItemIndex"
        v-bind="showProps"
        v-on="eventListeners"
        @edit="editItem"
        @delete="deleteItem"
        @add="addItem"
        @copy="cloneItem"
      />
      <BaseDialog
        fullscreen
        has-cancel
        v-model="showForm"
      >
        <template #title>
          {{ capitalize($t('general.edit')) }}
        </template>
        <template #content>
          <component
            v-if="currentItemIndex >= 0 && localValue[currentItemIndex]"
            :is="form"
            :initial-data="localValue[currentItemIndex]"
            v-model="localValue[currentItemIndex]"
            is-nested-form
            :custom-resource="resource"
            :show-data="currentItemShowData"
            v-bind="formProps"
            v-on="eventListeners"
          />
        </template>
      </BaseDialog>
    </template>
    <template v-else-if="undestroyedValues.length > 0">
      <div
        class="viewport-container"
        ref="viewportContainer"
        :style="{maxHeight: undestroyedValues.length > 10 ? '600px' : 'unset'}"
      >
        <v-card
          v-for="(item, index) in localValue"
          v-show="!item._destroy"
          :key="`row--${index}`"
          outlined
          class="nested-field-wrapper mr-6 mb-2"
          style="min-height: 100px;"
          v-observe-visibility="(isVisible) => handleVisibility(index, isVisible)"
        >
          <BaseRemoveButton @click="removeField(item)" />
          <v-row class="flex-nowrap" dense align="baseline" justify="center">
            <v-col>
              <template v-show="index > 20 ? visibleItems[index] : true">
                <component
                :is="form"
                v-model="localValue[index]"
                :initialData="item"
                is-nested-form
                :custom-resource="resource"
                :show-data="getShowData(item)"
                v-bind="formProps"
                v-on="eventListeners"
                />
              </template>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </template>
    <!-- TODO: Add expansion with slot for default styled/overriding item details -->
    <template v-if="!staticArray">
      <PromptDialog
        ref="prompt"
        :auto-resolve="!dialogOnRemove"
        :max-width="$vuetify.breakpoint.thresholds.xs"
      />
      <div class="d-flex" style="gap: 8px;">
        <ButtonAdd
          :text="formattedAddRowText"
          @click="addItem"
        />
        <ButtonAdd
          v-for="(properties, i) in additionalButtons"
          :key="`additional-button-${i}`"
          :text="properties.text"
          @click="addItemFromAdditionalButton(properties, i)"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { capitalize, cloneDeep } from 'lodash';
import PromptDialog from '@/components/PromptDialog.vue';
import ButtonAdd from '@/components/ButtonAdd.vue';

export default {
  name: 'NestedForm',
  components: {
    PromptDialog,
    ButtonAdd,
  },
  directives: {
    observeVisibility: {
      inserted(el, binding) {
        const options = {
          threshold: 0,
          rootMargin: '200px 0px',
          root: el.querySelector('.viewport-container')
        };
        
        // Wait for root element to be available
        setTimeout(() => {
          const observer = new IntersectionObserver(
            entries => binding.value(entries[0].isIntersecting),
            options
          );
          observer.observe(el);
          el._visibility_observer = observer;
        }, 0);
      },
      unbind(el) {
        if (el._visibility_observer) {
          el._visibility_observer.disconnect();
        }
      }
    },
  },
  props: {
    value: {
      type: null,
      required: false,
    },
    name: {
      type: String,
      default: undefined,
    },
    form: {
      // Form template
      type: Object,
      required: true,
    },
    showData: {
      type: [Object, Array],
      required: false,
    },
    show: {
      // Show template
      type: Object,
      required: false,
    },
    formProps: {
      type: Object,
      required: false,
    },
    showProps: {
      type: Object,
      required: false,
    },
    field: {
      type: Object,
      default: null,
    },
    resource: {
      type: String,
      required: true,
    },
    addRowByDefault: {
      type: Boolean,
      default: false,
    },
    addRowText: {
      type: String,
      default: undefined,
    },
    dialogOnRemove: {
      type: Boolean,
      default: false,
    },
    dialogItemText: {
      type: String,
      default: null,
    },
    staticArray: {
      // Set this prop if you don't want to allow adding/removing fields.
      type: Boolean,
      default: false,
    },
    valueFilter: {
      /**
       * Use this if you need to hide certain elements in the value
       * (e.g. filter polymorphic relations by type, or removed items)
       */
      type: Function,
      default: () => true,
    },
    additionalButtons: {
      type: Array,
      default: () => [],
    },
    triggerAddItem: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showForm: false,
      currentItem: undefined,
      currentItemIndex: -1,
      previousItemIndex: -1,
      visibleItems: {},
    };
  },
  watch: {
    showForm(val) {
      // Set current item to undefined when the form dialog is closed
      if (!val) this.setCurrentItem();
    },
    triggerAddItem: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal || newVal == oldVal) return;
        this.addItem(false);
      },
    },
    addRowByDefault: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal || newVal == oldVal) return;
        this.$nextTick(this.addItem);
      },
    },
  },
  computed: {
    currentItemShowData() {
      const currentItemId = this.currentItem?.id;
      return currentItemId && Array.isArray(this.showData)
        ? this.showData?.find(item => item.id == currentItemId)
        : undefined;
    },
    eventListeners() {
      const { input, ...listeners } = this.$listeners; // exclude `input`-listener
      return listeners;
    },
    localValue: {
      get() {
        if (this.valueFilter) {
          return this.value;
        } else {
          return this.value;
        }
      },
      set(v) {
        this.$emit('input', v);
      }
    },
    iconSize() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 17;
        case 'sm':
        case 'md':
        case 'lg':
        default:
          return 19;
      }
    },
    formattedAddRowText() {
      return this.addRowText || this.$t('buttons.add_row');
    },
    undestroyedValues() {
      return this.localValue?.filter(x => !x._destroy) || [];
    },
    destroyedValues() {
      return this.localValue?.filter(x => x._destroy) || [];
    },
  },
  created() {
    if (!Array.isArray(this.localValue))
      this.localValue = [];
  },
  methods: {
    capitalize,
    handleVisibility(index, isVisible) {
      this.$set(this.visibleItems, index, isVisible);
    },
    setCurrentItem(index = -1) {
      this.previousItemIndex = this.currentItemIndex;
      this.currentItemIndex = index;
      this.currentItem = index >= 0 ? this.localValue[index] : undefined;
      this.showForm = index >= 0;
    },
    editItem(index) {
      this.setCurrentItem(index);
    },
    deleteItem(index) {
      this.$emit('remove', this.localValue[index]);
      this.$set(this.localValue[index], '_destroy', 1);
    },
    addItem(openShowForm = true) {
      const newItem = {};
      this.localValue?.push(newItem);
      this.$emit('add', newItem); // $set keys in parent to provide defaults

      // Add scroll behavior
      const container = this.$refs.viewportContainer;
      if (container) {
        this.$nextTick(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        });
      }

      // Open show form when a custom show is used (and the dialog for the form)
      if (this.show && openShowForm) {
        const index = this.localValue?.length - 1;
        if (index < 0) return;
        this.setCurrentItem(index);
        this.showForm = true;
      }
    },
    cloneItem(index) {
      const copy = cloneDeep(this.localValue[index]);
      this.$emit('clone', copy);
      this.setCurrentItem((this.localValue?.length || 0) - 1);
    },
    addItemFromAdditionalButton(_properties, i=0) {
      const newItem = {};
      this.localValue?.push(newItem);
      this.$emit(`add:additional${i+1}`, newItem); // $set keys in parent to provide defaults
      if (this.show) {
        const index = this.localValue?.length - 1;
        if (index < 0) return;
        this.setCurrentItem(index);
        this.showForm = true;
      }
    },
    removeField(item) {
      const removeAction = () => {
        const index = this.itemIndex(item);
        if (index >= 0) {
          this.$emit('remove', this.localValue[index]);
          this.$set(this.localValue[index], '_destroy', 1);
        }
      };

      if (this.dialogOnRemove) {
        this.$refs.prompt.show(this.genPrompt(item)).then(removeAction);
      } else {
        removeAction();
      }
    },
    restoreField(item) {
      const index = this.itemIndex(item);
      if (index >= 0) {
        this.$emit('restore', this.localValue[index]);
        this.$set(this.localValue[index], '_destroy', 0);
      }
    },
    itemIndex(item) {
      if (!item) return -1;
      return this.localValue?.indexOf(item);
    },
    removeAllFields() {
      this.localValue.forEach(this.removeField);
    },
    restoreAllFields() {
      this.localValue.forEach(this.restoreField);
    },
    // updateField(newItem, item) pass to prop and call updateField from there, or make v-model work
    genPrompt(item) {
      if (!this.dialogOnRemove) return;

      let promptOpts = {};

      const itemText =
        this.dialogItemText ||
        item.name ||
        this.$t('components.form_field_list.this_item');

      promptOpts.message = this.$t(
        'components.form_field_list.want_to_remove_x',
        [itemText]
      );

      return promptOpts;
    },
    getShowData(item) {
      return Array.isArray(this.showData)
        ? this.showData.find(record => record.id === item.id)
        : undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
.viewport-container {
  position: relative;
  width: 100%;
  /*min-height: 200px;*/
  max-height: 600px; 
  height: auto;
  overflow-y: auto;
}

.viewport-scroll {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-x: hidden;
}

.nested-field-wrapper {
  position: relative;
  border-left: solid 4px var(--v-secondary-base);
}
.options {
  display: flex;
  flex-wrap: wrap-reverse;
  gap: 8px;
  text-align: right;
}
</style>
